import React from "react";

const TermsCondition = () => {
  return (
    <div className="terms-container">
      {/* Hero Section */}
      <div className="terms-hero">
        <div className="hero-content">
          <h1 className="terms-title">Terms and Conditions</h1>
          <p className="hero-subtitle">Last updated: August 2023</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="terms-content">
        {/* OVERVIEW */}
        <section className="terms-section">
          <div className="section-header">
            <div className="section-icon">📜</div>
            <h2 className="section-heading">Overview</h2>
          </div>
          <div className="section-body">
            <p>
              These Terms and Conditions are a legal agreement between you, as a current or prospective
              customer of Loops E-Commerce ("Loops", "we", "us", "our"), and you, the user
              ("you", "your"), to govern your use of Loops' services, including our website, mobile
              applications, software, and other related services (collectively, the "Services").
            </p>
            <p>
              By signing up for a Loops account, visiting our website, and/or making a purchase from our
              platform, you agree to be bound by these Terms and Conditions, including any additional rules,
              policies, and guidelines referenced herein.
            </p>
            <div className="highlight-box">
              <p>
                <strong>Important:</strong> Please read these Terms carefully before using our Services. If
                you do not agree to all terms, you may not access the website or use any Services.
              </p>
            </div>
          </div>
        </section>

        {/* ACCOUNT DETAILS */}
        <div className="terms-grid">
          {/* ACCOUNT REGISTRATION */}
          <div className="grid-item">
            <section className="terms-section card">
              <div className="section-header">
                <div className="section-icon">👤</div>
                <h2 className="section-heading">Account Registration</h2>
              </div>
              <div className="section-body">
                <p>
                  To make purchases or access certain features of Loops, you must create an account ("Loops
                  Account"). You must provide accurate and complete information and keep it updated.
                </p>
                <p>
                  You are responsible for all activity that occurs under your account, including actions by
                  anyone you authorize to access it.
                </p>
              </div>
            </section>
          </div>

          {/* ACCOUNT CANCELLATION */}
          <div className="grid-item">
            <section className="terms-section card">
              <div className="section-header">
                <div className="section-icon">❌</div>
                <h2 className="section-heading">Account Cancellation</h2>
              </div>
              <div className="section-body">
                <p>
                  Loops may cancel accounts that violate these Terms. In such cases, we may retain personal data for 
                  one month from cancellation for legal or security purposes.
                </p>
                <p>
                  If you close your account voluntarily, we'll delete your personal information except where 
                  required by law.
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* CONFIDENTIALITY */}
        <section className="terms-section">
          <div className="section-header">
            <div className="section-icon">🔒</div>
            <h2 className="section-heading">Confidentiality</h2>
          </div>
          <div className="section-body">
            <p>
              You are responsible for maintaining the confidentiality of your login credentials. Loops will
              assume that any activity carried out using your account credentials is authorized by you.
            </p>
            <p className="contact-notice">
              If you suspect unauthorized access, notify us immediately at <span className="highlight">support@loops.com</span>.
            </p>
          </div>
        </section>

        {/* DATA PRIVACY */}
        <section className="terms-section accent-section">
          <div className="section-header">
            <div className="section-icon">🛡️</div>
            <h2 className="section-heading">Data Privacy & Security</h2>
          </div>
          <div className="section-body">
            <p>
              Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect
              your information in compliance with applicable laws. By using our Services, you agree to our
              Privacy Policy.
            </p>
          </div>
        </section>

        {/* PERMITTED ACTIVITIES */}
        <section className="terms-section">
          <div className="section-header">
            <div className="section-icon">✅</div>
            <h2 className="section-heading">Permitted Activities</h2>
          </div>
          <div className="section-body">
            <ul className="styled-list">
              <li>You will not use our Services in violation of any laws or these Terms.</li>
              <li>You will not upload harmful content such as viruses or malicious code.</li>
              <li>You will not impersonate others or mislead about your identity.</li>
              <li>You will not copy, distribute, or use our content without permission.</li>
              <li>You will not interfere with or disrupt our Services or infrastructure.</li>
              <li>You will not sell or sublicense any materials from Loops without written consent.</li>
            </ul>
          </div>
        </section>

        {/* USER CONTENT */}
        <div className="terms-grid">
          {/* USER GENERATED CONTENT */}
          <div className="grid-item">
            <section className="terms-section card">
              <div className="section-header">
                <div className="section-icon">💬</div>
                <h2 className="section-heading">User Generated Content</h2>
              </div>
              <div className="section-body">
                <p>
                  Users may post reviews, comments, or other content on Loops. We do not pre-screen user
                  content, and it represents the views of the individuals who post it.
                </p>
                <p>
                  Loops reserves the right to monitor and remove any content deemed inappropriate.
                </p>
              </div>
            </section>
          </div>

          {/* THIRD-PARTY CONTENT */}
          <div className="grid-item">
            <section className="terms-section card">
              <div className="section-header">
                <div className="section-icon">🔗</div>
                <h2 className="section-heading">Third-Party Content</h2>
              </div>
              <div className="section-body">
                <p>
                  Our Services may contain links or content from third parties. Loops is not responsible for
                  third-party websites, products, or services.
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* DISCLAIMER */}
        <section className="terms-section warning-section">
          <div className="section-header">
            <div className="section-icon">⚠️</div>
            <h2 className="section-heading">Disclaimer</h2>
          </div>
          <div className="section-body">
            <p>
              To the fullest extent permitted by law, Loops provides the Services "as is" without any
              warranties, express or implied. We do not guarantee uninterrupted access, error-free
              operation, or that the Services will be free from harmful components.
            </p>
            <ul className="styled-list">
              <li>We do not limit or exclude liability for death or personal injury caused by negligence.</li>
              <li>We do not limit liability for fraud or fraudulent misrepresentation.</li>
              <li>We do not exclude any liability that cannot be excluded under applicable law.</li>
            </ul>
          </div>
        </section>

        {/* CLOSING */}
        <section className="terms-closing">
          <p>
            By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
          </p>
          <p className="closing-contact">
            For any questions regarding these Terms, please contact us at <span className="highlight">legal@loops.com</span>.
          </p>
        </section>
      </div>

      <style jsx>{`
        .terms-container {
          background: #ffffff;
          color: #333;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0;
        }

        .terms-hero {
          background: linear-gradient(135deg, #6e8efb 0%, #4a6cf7 100%);
          color: white;
          padding: 4rem 2rem;
          text-align: center;
          border-radius: 0 0 20px 20px;
          margin-bottom: 3rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .terms-title {
          font-size: 2.8rem;
          font-weight: 800;
          margin-bottom: 0.5rem;
          color: white;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          opacity: 0.9;
          font-weight: 400;
        }

        .terms-content {
          padding: 0 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }

        .terms-section {
          margin-bottom: 3rem;
          position: relative;
        }

        .terms-section.card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border: 1px solid #eee;
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .terms-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
          align-items: stretch;
        }

        .grid-item {
          display: flex;
        }

        .section-body {
          flex: 1;
        }

        @media (max-width: 768px) {
          .terms-grid {
            grid-template-columns: 1fr;
          }
        }

        .section-header {
          display: flex;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .section-icon {
          font-size: 1.8rem;
          margin-right: 1rem;
          opacity: 0.8;
        }

        .section-heading {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2c3e50;
          margin: 0;
        }

        .section-body p {
          margin-bottom: 1rem;
          color: #4f4f4f;
          font-size: 1.05rem;
        }

        .highlight-box {
          background: #f8f9ff;
          border-left: 4px solid #4a6cf7;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0 8px 8px 0;
        }

        .highlight-box p {
          margin: 0;
          color: #4a6cf7;
        }

        .styled-list {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0;
        }

        .styled-list li {
          padding: 0.5rem 0 0.5rem 1.8rem;
          position: relative;
          margin-bottom: 0.5rem;
        }

        .styled-list li:before {
          content: "•";
          color: #4a6cf7;
          font-weight: bold;
          position: absolute;
          left: 0;
        }

        .contact-notice {
          background: #f0f4ff;
          padding: 1rem;
          border-radius: 8px;
          font-weight: 500;
        }

        .highlight {
          color: #4a6cf7;
          font-weight: 600;
        }

        .accent-section {
          background: #f8f9ff;
          padding: 2rem;
          border-radius: 12px;
          border-left: 4px solid #4a6cf7;
        }

        .warning-section {
          background: #fff8f8;
          padding: 2rem;
          border-radius: 12px;
          border-left: 4px solid #ff6b6b;
        }

        .warning-section .section-icon {
          color: #ff6b6b;
        }

        .terms-closing {
          text-align: center;
          margin: 4rem 0 0 0;
          padding: 2rem 0 0 0;
          border-top: 1px solid #eee;
        }

        .closing-contact {
          font-weight: 500;
          margin-top: 1.5rem;
        }

        @media (max-width: 768px) {
          .terms-hero {
            padding: 3rem 1rem;
          }
          
          .terms-title {
            font-size: 2rem;
          }
          
          .terms-content {
            padding: 0 1rem;
          }
          
          .section-heading {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsCondition;